import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Database } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(b) {
  if (b == null) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = Number(b);
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

export default function DbSizeDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingBaseline, setEditingBaseline] = useState(false);
  const [baselineInput, setBaselineInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/db-size`);
      setData(data);
      setBaselineInput(String(data.baseline_bytes || 0));
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const saveBaseline = async () => {
    const v = parseInt(baselineInput, 10);
    if (Number.isNaN(v) || v < 0) {
      toast.error("ΛΑΘΟΣ ΤΙΜΗ");
      return;
    }
    try {
      await axios.put(`${API}/admin/db-size/baseline`, { bytes: v });
      toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
      setEditingBaseline(false);
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[560px] rounded-2xl p-0 overflow-hidden border-none"
        data-testid="db-size-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              <Database size={24} weight="bold" />
              ΜΕΓΕΘΟΣ ΒΑΣΗΣ
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΣΥΝΟΛΟ / ΑΥΞΗΣΗ ΑΠΟ BASELINE
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 bg-white space-y-5">
          {loading || !data ? (
            <p className="text-xs text-ink/50 italic">ΦΟΡΤΩΣΗ...</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="ΣΥΝΟΛΟ" value={fmtBytes(data.total_bytes)} accent />
                <Stat label="BASELINE" value={fmtBytes(data.baseline_bytes)} />
                <Stat
                  label="ΑΥΞΗΣΗ"
                  value={
                    data.delta_bytes >= 0
                      ? `+${fmtBytes(data.delta_bytes)}`
                      : `−${fmtBytes(-data.delta_bytes)}`
                  }
                  color={
                    data.delta_bytes > 0
                      ? "text-amber-700"
                      : data.delta_bytes < 0
                      ? "text-green-700"
                      : "text-ink"
                  }
                />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-2">
                  ΑΝΑ COLLECTION
                </div>
                <div className="space-y-1.5">
                  {Object.entries(data.collections || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, size]) => (
                      <div
                        key={name}
                        className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-white border border-black/5 text-xs font-arial"
                      >
                        <span className="text-ink/75 truncate">
                          {name.replace("pinboard_", "")}
                        </span>
                        <span className="font-bold text-ink tabular-nums">
                          {fmtBytes(size)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="pt-3 border-t border-black/5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-2">
                  ΟΡΙΣΜΟΣ BASELINE (BYTES)
                </div>
                <div className="flex items-stretch gap-2">
                  <input
                    type="number"
                    min={0}
                    value={baselineInput}
                    disabled={!editingBaseline}
                    onChange={(e) => setBaselineInput(e.target.value)}
                    data-testid="db-baseline-input"
                    className={`flex-1 px-3 py-2 rounded-xl border outline-none text-sm font-arial tabular-nums ${
                      editingBaseline
                        ? "border-ink/50 bg-white"
                        : "border-ink/10 bg-black/5"
                    }`}
                  />
                  {editingBaseline ? (
                    <>
                      <button
                        onClick={saveBaseline}
                        data-testid="db-baseline-save"
                        className="px-4 rounded-xl text-xs font-bold uppercase tracking-wider bg-ink text-white hover:bg-ink/85"
                      >
                        ΑΠΟΘ.
                      </button>
                      <button
                        onClick={() => {
                          setEditingBaseline(false);
                          setBaselineInput(String(data.baseline_bytes || 0));
                        }}
                        className="px-3 rounded-xl text-xs font-bold uppercase tracking-wider text-ink/60 hover:text-ink"
                      >
                        ΑΚΥΡΟ
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingBaseline(true)}
                        data-testid="db-baseline-edit"
                        className="px-4 rounded-xl text-xs font-bold uppercase tracking-wider bg-white border border-ink/15 hover:border-ink/40"
                      >
                        ΕΠΕΞΕΡΓΑΣΙΑ
                      </button>
                      <button
                        onClick={() => {
                          setBaselineInput(String(data.total_bytes));
                          setEditingBaseline(true);
                        }}
                        title="Όρισμα baseline = τρέχον μέγεθος"
                        className="px-4 rounded-xl text-xs font-bold uppercase tracking-wider bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200"
                      >
                        ΟΡΙΣΕ ΩΣ ΤΩΡΙΝΟ
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, color }) {
  return (
    <div
      className={`rounded-xl px-3 py-3 border ${
        accent ? "bg-ink/5 border-ink/15" : "bg-white border-black/10"
      }`}
    >
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-ink/55">
        {label}
      </div>
      <div className={`text-base font-black tabular-nums mt-1 ${color || "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}
